import { Select, MenuItem, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TextBlock } from '../../../Atoms/Text';
import { SearchBox } from '../../Search/Search';
import { EntityType, SearchResult } from '../../../../store/Search';
import { TicketInquiryType, TicketType } from '../../../../store/Ticket';
import { CustomActionBar } from '../../../Compounds/DetailPanel/CustomActionBar';
import { getUTCRunDate } from '../../../../utils/helpers';

export interface FormData {
  ticketType: TicketType;
  inquiryType: TicketInquiryType;
  supplier: string;
  supplierName: string;
  tsd: string;
  tsdName: string;
  firstRun: string | null;
  receivedDate: string | null;
  referenceLinkedObjects: boolean;
  proactive: boolean;
}

interface TicketFormFieldsProps {
  firstRunDate: Date | null;
  receivedDate: Date | null;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  isSubmitting: boolean;
  objectType: 'asset' | 'provisionalAsset' | 'rpmAccount' | 'customer';
  setFirstRunDate: React.Dispatch<React.SetStateAction<Date | null>>;
  setReceivedDate: React.Dispatch<React.SetStateAction<Date | null>>;
  showSupplierSearch: boolean;
  showSupplierSelect: boolean;
  supplierOptions: { value: string; label: string }[];
}

export const TicketFormFields = ({
  firstRunDate,
  receivedDate,
  formData,
  setFormData,
  isSubmitting,
  objectType,
  setFirstRunDate,
  setReceivedDate,
  showSupplierSearch,
  showSupplierSelect,
  supplierOptions
}: TicketFormFieldsProps) => {
  const handleTicketTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevData) => ({
      ...prevData,
      ticketType: event.target.value as TicketType,
      inquiryType:
        event.target.value === TicketType.SOURCE
          ? TicketInquiryType.INFORMATION_REQUEST
          : TicketInquiryType.ACCOUNT_DROPPED_OFF
    }));
  };

  const handleChange = (fieldName: keyof FormData, value: string) => {
    setFormData((prevData) => ({ ...prevData, [fieldName]: value }));
  };

  const handleChangeBool = (fieldName: keyof FormData, value: string) => {
    const boolValue = value === 'true';
    setFormData((prevData) => ({ ...prevData, [fieldName]: boolValue }));
  };

  const handleSelect = (result: SearchResult, field: 'supplier' | 'tsd') => {
    const { id, title } = result;
    setFormData((prevData) => ({
      ...prevData,
      [field]: id,
      [`${field}Name`]: title
    }));
  };

  const handleDateChange = (selectedDate: Date) => {
    setFirstRunDate(getUTCRunDate(selectedDate));
  };

  const handleReceivedDateChange = (selectedDate: Date) => {
    setReceivedDate(new Date(selectedDate));
  };

  const inquiryTypeOptions =
    formData.ticketType === TicketType.SOURCE
      ? [
          TicketInquiryType.INFORMATION_REQUEST,
          TicketInquiryType.COMMISSION_PERCENT,
          TicketInquiryType.PAYMENT_QUESTION,
          TicketInquiryType.REPORTING_QUESTION
        ]
      : [
          TicketInquiryType.ACCOUNT_DROPPED_OFF,
          TicketInquiryType.ORDER_TRACKING_INQUIRY,
          TicketInquiryType.ACCOUNT_BILLING_DECREASED,
          TicketInquiryType.CHARGEBACK_QUESTION,
          TicketInquiryType.BILLING_QUESTION,
          TicketInquiryType.INFORMATION_REQUEST,
          TicketInquiryType.COMMISSION_PERCENT,
          TicketInquiryType.SPIFF_MISSING
        ];

  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
      <div className="space-y-1">
        <div className="space-y-1 justify-center">
          <TextBlock
            color="grey6"
            size="sm">
            Ticket Type
          </TextBlock>
          <RadioGroup
            aria-label="ticketType"
            name="ticketType"
            value={formData.ticketType}
            onChange={handleTicketTypeChange}>
            <FormControlLabel
              value={TicketType.ASSETS_AND_RPM_ACCOUNTS}
              control={
                <Radio
                  disabled
                  sx={{ height: '1.2rem' }}
                />
              }
              label="Assets and RPM Accounts"
            />
            <FormControlLabel
              value={TicketType.SOURCE}
              control={<Radio disabled />}
              label="Source"
            />
          </RadioGroup>
        </div>
        {showSupplierSearch && (
          <div className="space-y-1 justify-center">
            <TextBlock
              color="grey6"
              size="sm">
              Supplier
            </TextBlock>
            <SearchBox
              handleSelect={(result) => handleSelect(result, 'supplier')}
              searchEntityType={EntityType.SUPPLIER}
              inputString={formData.supplierName}
              placeholder={formData.supplierName}
              disabled={isSubmitting}
              fullWidth
            />
          </div>
        )}
        {showSupplierSelect && (
          <div className="space-y-1">
            <TextBlock
              color="grey6"
              size="sm">
              Supplier
            </TextBlock>
            <Select
              id="supplier"
              value={formData.supplier}
              sx={{ width: '100%', height: '2.4rem' }}
              disabled={isSubmitting}
              onChange={(e) => {
                const val = e.target.value;
                const label = supplierOptions.find((i) => i.value == val)?.label;
                setFormData({
                  ...formData,
                  supplier: val,
                  supplierName: label || ''
                });
              }}>
              {supplierOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
        <div className="space-y-1 justify-center">
          <TextBlock
            color="grey6"
            size="sm">
            TSD
          </TextBlock>
          <SearchBox
            handleSelect={(result) => handleSelect(result, 'tsd')}
            searchEntityType={EntityType.TSD}
            inputString={formData.tsdName}
            placeholder={formData.tsdName}
            disabled={isSubmitting}
            fullWidth
          />
        </div>
      </div>
      <div className="space-y-4">
        <div className="space-y-1 justify-center">
          <TextBlock
            color="grey6"
            size="sm">
            Inquiry Type
          </TextBlock>
          <Select
            labelId="inquiry-type-label"
            id="inquiry-type"
            value={formData.inquiryType}
            disabled={isSubmitting}
            sx={{ width: '100%', height: '2.4rem' }}
            onChange={(e) => handleChange('inquiryType', e.target.value as TicketInquiryType)}>
            {inquiryTypeOptions.map((option) => (
              <MenuItem
                key={option}
                value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className="space-y-1 justify-center">
          <TextBlock
            color="grey6"
            size="sm">
            First Run
          </TextBlock>
          <DatePicker
            value={firstRunDate}
            onChange={(newValue) => handleDateChange(newValue || new Date())}
            slots={{
              actionBar: CustomActionBar
            }}
            slotProps={{
              actionBar: { actions: ['next', 'accept', 'cancel'] },
              textField: { size: 'small', fullWidth: true }
            }}
            views={['month', 'year']}
            sx={{ mr: 2, mt: 2 }}
            disabled={isSubmitting}
            format="MMM yyyy"
          />
        </div>
        <div className="space-y-1 justify-center">
          <TextBlock
            color="grey6"
            size="sm">
            Received Date
          </TextBlock>
          <DatePicker
            value={receivedDate || new Date()}
            onChange={(newValue) => handleReceivedDateChange(newValue || new Date())}
            slotProps={{
              actionBar: { actions: ['today'] },
              textField: { size: 'small', fullWidth: true }
            }}
            sx={{ mr: 2, mt: 2, width: '100%' }}
            disabled={isSubmitting}
            format="MM/dd/yyyy"
          />
        </div>
        <div className="space-y-1 justify-center">
          <TextBlock
            color="grey6"
            size="sm">
            Proactive
          </TextBlock>
          <Select
            labelId="proactive-label"
            id="proactive"
            value={String(formData.proactive)}
            disabled={isSubmitting}
            sx={{ width: '100%', height: '2.4rem' }}
            onChange={(e) => handleChangeBool('proactive', e.target.value as string)}>
            {[true, false].map((option) => (
              <MenuItem
                key={String(option)}
                value={String(option)}>
                {option ? 'Yes' : 'No'}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
};
